/**
 * Course Invitation Service
 *
 * Frontend service for managing course invitations between instructors and learners
 */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  CourseInvitation,
  InvitationStats,
  InvitationRequest,
  InvitationResponse,
  BulkInvitationResult
} from '../types/invitation.types';
import { PaginationService, PaginatedResponse } from './pagination.service';

export interface SentInvitationsPageOptions {
  page: number;
  pageSize: number;
  courseId?: string;
  status?: string;
}

export interface ReceivedOrgInvitationsPageOptions {
  page: number;
  pageSize: number;
  status?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CourseInvitationService {
  private apiUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private paginationService: PaginationService
  ) {}

  /**
   * Send a course invitation.
   *
   * Email Wave 2: unknown emails create a tokenized NEW-USER invitation
   * server-side; the response carries { invitation, emailSent, emailError }.
   * `personalNote` (2,000 semantic-character limit, sanitized server-side) is included in the
   * invite email; `message` is kept for backward compatibility.
   */
  sendInvitation(courseId: string, learnerEmail: string, message?: string, personalNote?: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/invitations`, {
      courseId,
      learnerEmail,
      message,
      personalNote
    });
  }

  /**
   * Re-send the invite email for a pending course invitation (Email Wave 2).
   * New-user invites get a fresh signup token server-side.
   */
  resendInvitation(invitationId: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/invitations/${invitationId}/resend`, {});
  }

  /**
   * Re-send the invite email for a pending organization invitation (Email Wave 2).
   */
  resendOrganizationInvitation(invitationId: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/organization-invitations/${invitationId}/resend`, {});
  }

  /**
   * Cancel an invitation
   */
  cancelInvitation(invitationId: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/invitations/${invitationId}/cancel`, {});
  }


  /**
   * Search users for invitation
   * @param searchTerm - Search query for name or email
   * @param courseId - Current course ID to filter out enrolled users
   * @param filter - 'organization' for org members only, 'external' for non-org members
   * @param organizationId - Specific org ID or 'all' for all instructor's orgs
   */
  searchUsers(
    searchTerm: string,
    courseId?: string,
    filter: 'organization' | 'external' = 'organization',
    organizationId?: string | 'all'
  ): Observable<any[]> {
    const params: any = {
      search: searchTerm,
      filter: filter
    };

    if (courseId) {
      params.courseId = courseId;
    }

    if (organizationId) {
      params.organizationId = organizationId;
    }

    return this.http.get<{data: {users: any[]}}>(`${this.apiUrl}/invitations/search-users`, {
      params: params
    }).pipe(
      map(response => {
        console.log('Raw API response:', response);
        return response.data?.users || [];
      })
    );
  }

  /**
   * Send invitations to selected learners
   */
  sendInvitations(courseId: string, request: InvitationRequest): Observable<BulkInvitationResult> {
    return this.http.post<any>(
      `${this.apiUrl}/courses/${courseId}/invitations/send`,
      request
    ).pipe(
      map(response => {
        console.log('Send invitation API response:', response);
        console.log('Response data object:', response.data);
        console.log('Response data type:', typeof response.data);

        // Handle different response formats
        let invitations = [];
        if (response.data && response.data.invitations && Array.isArray(response.data.invitations)) {
          invitations = response.data.invitations;  // Backend returns response.data.invitations
        } else if (response.invitations && Array.isArray(response.invitations)) {
          invitations = response.invitations;
        } else if (response.data && Array.isArray(response.data)) {
          invitations = response.data;
        } else if (Array.isArray(response)) {
          invitations = response;
        }

        console.log('Parsed invitations array:', invitations);

        return {
          totalSent: invitations.length,
          successful: invitations,
          failed: []
        };
      })
    );
  }

  /**
   * All sent invitations for a course (pagination migration P6b-A).
   * Walks getSentInvitationsPaginated page by page (limit 100) and emits one
   * concatenated array — replaces the legacy no-param full-list request.
   * @param courseId Optional - if provided, filters to specific course
   */
  getAllSentInvitations(courseId?: string): Observable<CourseInvitation[]> {
    return this.paginationService.fetchAllPages<CourseInvitation>(
      (page, pageSize) => this.getSentInvitationsPaginated({ page, pageSize, courseId })
    );
  }

  /**
   * One server page of sent invitations (pagination migration P4).
   * Opts in to the backend pagination envelope via page/limit params.
   */
  getSentInvitationsPaginated(options: SentInvitationsPageOptions): Observable<PaginatedResponse<CourseInvitation>> {
    const params: Record<string, string> = {
      page: options.page.toString(),
      limit: options.pageSize.toString()
    };
    if (options.courseId) params['courseId'] = options.courseId;
    if (options.status) params['status'] = options.status;

    return this.http.get<any>(`${this.apiUrl}/invitations`, { params }).pipe(
      map(response => this.paginationService.fromEnvelope<CourseInvitation>(response.data ?? response))
    );
  }

  /**
   * Paginated variant of getReceivedInvitations (pagination migration P4).
   * Backend keeps the pending-only filter; this just pages through it.
   */
  getReceivedInvitationsPaginated(page: number, pageSize: number): Observable<PaginatedResponse<CourseInvitation>> {
    const params = { page: page.toString(), limit: pageSize.toString() };

    return this.http.get<any>(`${this.apiUrl}/invitations/received`, { params }).pipe(
      map(response => this.paginationService.fromEnvelope<CourseInvitation>(response.data ?? response))
    );
  }

  /**
   * Paginated variant of getReceivedOrganizationInvitations (pagination migration P4).
   * Supports a server-side status filter (e.g. 'pending') applied to both the
   * COUNT and data queries on the backend.
   */
  getReceivedOrganizationInvitationsPaginated(options: ReceivedOrgInvitationsPageOptions): Observable<PaginatedResponse<any>> {
    const params: Record<string, string> = {
      page: options.page.toString(),
      limit: options.pageSize.toString()
    };
    if (options.status) params['status'] = options.status;

    return this.http.get<any>(`${this.apiUrl}/organization-invitations/received`, { params }).pipe(
      map(response => this.paginationService.fromEnvelope<any>(response.data ?? response))
    );
  }

  /**
   * Respond to course invitation
   */
  respondToInvitation(invitationId: string, response: InvitationResponse): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/invitations/${invitationId}/respond`, response);
  }

  /**
   * Delete invitation (instructor only)
   */
  deleteInvitation(invitationId: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/invitations/${invitationId}`);
  }

  /**
   * Get invitation statistics for notification badges (pagination migration
   * P6b-A). Counts come from `pagination.total` of limit-1 paginated requests
   * so the badge shows TOTAL pending invitations, never a first-page length:
   * - course invitations: /invitations/received is pending-only server-side
   * - org invitations: server-side status=pending filter
   */
  getInvitationStats(): Observable<InvitationStats> {
    return forkJoin({
      pendingCourseTotal: this.getReceivedInvitationsPaginated(1, 1).pipe(
        map(page => page.total),
        catchError(error => {
          console.error('Error fetching course invitations for stats:', error);
          return of(0);
        })
      ),
      pendingOrgTotal: this.getReceivedOrganizationInvitationsPaginated({ page: 1, pageSize: 1, status: 'pending' }).pipe(
        map(page => page.total),
        catchError(error => {
          console.error('Error fetching org invitations for stats:', error);
          return of(0);
        })
      )
    }).pipe(
      map(results => ({
        pendingReceived: results.pendingCourseTotal + results.pendingOrgTotal,
        pendingSent: 0,
        recentAccepted: 0,
        recentRejected: 0
      }))
    );
  }

  /**
   * Get invitation details by ID
   */
  getInvitationById(invitationId: string): Observable<CourseInvitation> {
    return this.http.get<CourseInvitation>(`${this.apiUrl}/invitations/${invitationId}`);
  }

  /**
   * Respond to organization invitation
   */
  respondToOrganizationInvitation(invitationId: string, action: 'accept' | 'reject'): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/organization-invitations/${invitationId}/respond`, { action });
  }
}
